import {
  API,
  APIError,
  currentEnv,
  encodeURLString,
  ErrorHandling,
  EVENT_APP_INITIAL_LOADING_COMPLETE,
  EVENT_REQUEST_APP_AVATAR_STATE,
  EVENT_REQUEST_EXIT_MONSTER_WORLD,
  FatalErrors,
  FeatureSwitchManager,
  fetchWrapper,
  getLauncherStartupOptions,
  HandlingMethod,
  isIOSAppEnv,
  Logger,
  patchFetch,
  sharedErrorHandlingCases,
  WebGL2Error,
} from "@classdojo/monsterverse-utils";

import { getAWSHostURLs, handleUserDataRequests } from "./AWSHostURLs";
import { handleIFrameMessages, postToApp } from "./MessageHandler";
import { Networking } from "./Networking";
import { fatalErrorIfOutage, OutageError } from "./Outage";
import { shim } from "./Shim";
import { handleFatalErrors } from "./UI/FatalErrorModal";
import { fatalErrorIfNoWasm, WasmError } from "./Wasm";
import { fatalErrorIfNoWebGL2 } from "./WebGL2";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let networking: Networking | undefined;

// eslint-disable-next-line sonarjs/cognitive-complexity
async function main() {
  Logger.initialize(FeatureSwitchManager, { env: currentEnv(), customEndpoint: "/logs" });
  let hasLoggedFatalError = false;
  const returnUserToApp = (reason: string, error?: Error, unexpected?: boolean) => {
    if (!hasLoggedFatalError) {
      if (error && error instanceof APIError) {
        Logger.logInfo("Returning user to app", { reason, errorMsg: error?.message ?? "NULL", error: error.toJSON() });
      } else {
        Logger.logInfo("Returning user to app", { reason, errorMsg: error?.message ?? "NULL" });
      }
      Logger.flushLogs();
      hasLoggedFatalError = true;
    }
    postToApp({ eventName: EVENT_REQUEST_EXIT_MONSTER_WORLD, eventValues: [{ unexpected }] });
  };
  ErrorHandling.initialize()
    .registerErrorCase("Old IOS clients with non fatal error in resizeIfNeeded function", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return (
          error.message === "undefined is not an object (evaluating 'pc.app.graphicsDevice.canvas')" &&
          error.stack !== undefined &&
          error.stack.includes("resizeIfNeeded")
        );
      },
    })
    .registerErrorCase("Outage", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return error instanceof OutageError;
      },
    })
    .registerErrorCase("No Wasm", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return error instanceof WasmError;
      },
    })
    .registerErrorCase("No WebGL2", {
      method: HandlingMethod.DisregardError,
      isCondition: (error) => {
        return error instanceof WebGL2Error;
      },
    })
    .registerErrorCase("Fatal error caught during loading; sending user back to app.", {
      method: HandlingMethod.HandleError,
      isCondition: () => {
        return true;
      },
      onHandle: (error?: Error) => {
        returnUserToApp("LauncherFatalErrorWhileLoading", error, true);
      },
    })
    .registerErrorCases(
      ...sharedErrorHandlingCases((reason: string, error?: Error, unexpected?: boolean) => {
        returnUserToApp(`Launcher${reason}`, error, unexpected);
      })
    );
  shim();
  patchFetch();
  API.init("https://clients.multiplayer.classdojo.com", getLauncherStartupOptions().entityType, {
    Logger,
    ErrorHandling,
    FatalErrors,
    fetchWrapper,
  });
  Logger.incrementMetric("monsterverse.stats.launcher.open", 1, { platform: Logger.getPlatform() });
  const loadingEl = document.getElementById("application-launcher-splash-wrapper");
  if (!loadingEl) {
    return;
  }

  // iFrame preparation
  const iframeEl = document.getElementById("application-iframe");

  // Ensure the iframe is actually an iframe.
  if (!(iframeEl instanceof HTMLIFrameElement)) {
    return;
  }

  // iFrame message handling
  const pcAppEventHandler = handleIFrameMessages(iframeEl, loadingEl);
  handleFatalErrors(iframeEl, pcAppEventHandler);
  handleUserDataRequests(iframeEl);
  await fatalErrorIfOutage();

  const loadClient = async () => {
    const { entityType, islandId, startupParams, URLdata } = getLauncherStartupOptions();
    const { spawnPoint } = URLdata ?? {};
    Logger.setEntityType(entityType === "student" ? "studentUser" : entityType);

    const selfParams = new URL(window.location.href).searchParams;
    const bundleId = selfParams.get("bundle");
    const forceWebGL1 = selfParams.get("webgl1") ?? false;

    const { wsUrl, hubUrl, island, countryCode, serverRegion, desiredServerRegion, editor, auth, monsterConfig } =
      await getAWSHostURLs(islandId ?? "default", entityType, bundleId ?? undefined);

    // Fast check for required support (WebGL2, WASM)
    // We must call this after getAWSHostURLs because translations are loaded there
    fatalErrorIfNoWebGL2();
    fatalErrorIfNoWasm();

    Logger.setServerID(island.id);
    Logger.setCountryCode(countryCode);

    const urlParams = new URLSearchParams(window.location.search);
    const entityId = urlParams.get("entityId");
    const sessionId = urlParams.get("sessionId");
    const ghostMode = urlParams.get("ghost") === "true";

    console.log(urlParams.entries());
    console.log(forceWebGL1);
    if (sessionId) {
      Logger.setSessionId(sessionId);
    }

    const hubUrlBuilder = new URL(hubUrl);
    hubUrlBuilder.searchParams.set(
      "data",
      encodeURLString(JSON.stringify({ island, entityType, countryCode, spawnPoint, ghostMode, monsterConfig }))
    );
    hubUrlBuilder.searchParams.set("sessionId", sessionId ?? Logger.getSessionId());
    if (forceWebGL1) {
      console.log("Running in debug webgl1 mode");
      hubUrlBuilder.searchParams.set("webgl1", "true");
    }
    if (entityId) {
      Logger.setEntityId(entityId);
      hubUrlBuilder.searchParams.set("entityId", entityId);
    }

    hubUrlBuilder.searchParams.set("startupParams", startupParams);

    if (bundleId) {
      hubUrlBuilder.searchParams.set("bundle", bundleId);
    }

    if (editor) {
      hubUrlBuilder.searchParams.set("editor", "true");
    }

    hubUrlBuilder.searchParams.set("auth", auth);

    // Load iframe using the remove/change/re-add trick
    // This ensures it has no part in the browser history as navigating back
    // and forth shouldn't retain the iframe src at all as it needs to be recalculated with the ticket lambda
    const parent = iframeEl.parentElement;
    iframeEl.remove();
    iframeEl.src = hubUrlBuilder.toString();
    parent?.appendChild(iframeEl);

    networking = new Networking(
      iframeEl,
      pcAppEventHandler,
      wsUrl,
      island.id,
      countryCode,
      serverRegion,
      desiredServerRegion
    );
  };

  //Client loading happens after the IOS host is ready
  //This ensures the host has registered the handshake messages before we try and fire them
  if (isIOSAppEnv()) {
    let appAvatarStateRegistered = false;
    let appInitialLoadingRegistered = false;
    pcAppEventHandler.onRegistration(EVENT_REQUEST_APP_AVATAR_STATE, () => {
      appAvatarStateRegistered = true;
      if (appInitialLoadingRegistered) {
        loadClient();
      }
    });
    pcAppEventHandler.onRegistration(EVENT_APP_INITIAL_LOADING_COMPLETE, () => {
      appInitialLoadingRegistered = true;
      if (appAvatarStateRegistered) {
        loadClient();
      }
    });
  } else {
    loadClient();
  }
}

main();
